import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import NavLink from './common/NavLink';
import CustomSelect from './CustomSelect';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, FormControl, Select, MenuItem } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RequireVip from './RequireVip'
import { auth } from './Firebase'; // Dodano import auth z Firebase
import { useCompetitions } from './CompetitionContext';

const convertUTCToLocal = (utcValue) => {
  let utcDate;
  if (typeof utcValue === 'number') {
    utcDate = new Date(utcValue);
  } else if (typeof utcValue === 'string') {
    utcDate = new Date(utcValue + 'Z');
  } else {
    console.error('Nieprawidłowy typ created_at:', typeof utcValue, utcValue);
    return 'Błąd daty';
  }
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

const getGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)'; // Pojaśniony neutralny szary
  const scaledValue = Math.max(0, value / 0.8);
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); // Lekkie niebieskie dla głębi
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const PositionMatrix = () => {
  const { competitionId } = useParams();
  const { competitions, loading } = useCompetitions();
  const [data, setData] = useState({ teamData: {}, positionProbabilities: [], createdAt: null });
  const [leagueName, setLeagueName] = useState('');
  const [selectedSimulationId, setSelectedSimulationId] = useState(null);
  const [simulationList, setSimulationList] = useState([]);
  const [currentSimulationIndex, setCurrentSimulationIndex] = useState(0);
  const [error, setError] = useState(null);
  const [selectedComp, setSelectedComp] = useState(null); // ← Dodany stan dla selectedComp
  
  const simulationOptions = simulationList.map((sim, index) => ({
    value: index,
    label: sim.displayName,
  }));

  const simulationAbortControllerRef = useRef(null);

  const fetchSimulationData = useCallback(async (simulationId, uId) => {
    if (!simulationId) return;

    if (simulationAbortControllerRef.current) {
      simulationAbortControllerRef.current.abort();
    }

    simulationAbortControllerRef.current = new AbortController();
    try {
      const headers = uId ? { 'Authorization': `Bearer ${uId}` } : {};
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/simulation/${simulationId}`,
        { headers, signal: simulationAbortControllerRef.current.signal }
      );
      setData(response.data);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        setError('Błąd podczas pobierania danych symulacji.');
        console.error(err);
      }
    }
  }, []);

  useEffect(() => {
  if (loading || !competitionId) return;

  const selectedComp = Object.values(competitions)
    .flat()
    .find(comp => comp.competition_id === parseInt(competitionId));

  if (!selectedComp) {
    setError('Liga nie znaleziona.');
    return;
  }
  setSelectedComp(selectedComp); // ← Zapisujemy do stanu

  const displayName =
    selectedComp.group && selectedComp.group !== 'NULL' && selectedComp.group.trim() !== ''
      ? `${selectedComp.name} (${selectedComp.group.charAt(0).toUpperCase() + selectedComp.group.slice(1)})`
      : selectedComp.name;
  setLeagueName(displayName);

  // Pobieranie listy symulacji z nowego endpointu
  console.log('Pobieram symulacje dla competitionId:', competitionId);
  axios.get(`${process.env.REACT_APP_API_URL}/api/competition/${competitionId}/simulations`)
    .then(response => {
      console.log('Pobrano symulacje:', response.data);
      const simList = response.data.map(sim => ({
        ...sim,
		userId: sim.user_id, // Jawne mapowanie user_id na userId
        displayName: sim.conditions !== "" ? `${convertUTCToLocal(sim.created_at)} (*)` : convertUTCToLocal(sim.created_at)
      }));
	  const filteredSimList = simList.filter(sim => {
      const isDefault = !sim.userId || sim.userId === ""; // null lub pusty string = default
      const isOwned = auth.currentUser?.uid && sim.userId === auth.currentUser.uid; // Tylko dla zalogowanego
      console.log(`Sim: ${sim.simulation_id}, userId: ${sim.userId}, isDefault: ${isDefault}, isOwned: ${isOwned}`); // Debug
      return isDefault || isOwned;
    });
      setSimulationList(filteredSimList);
    const initialIndex = 0;
    setCurrentSimulationIndex(initialIndex);
    const initialSimId = filteredSimList.length > 0 ? filteredSimList[initialIndex].simulation_id : null;
    setSelectedSimulationId(initialSimId);
    console.log('Filtered simList:', filteredSimList); // Debug po filtrze
    })
    .catch(err => {
      setError('Błąd podczas pobierania listy symulacji.');
      console.error('Błąd pobierania symulacji:', err.response?.data || err.message);
    });
}, [competitionId, competitions, loading]);

  useEffect(() => {
    if (!selectedSimulationId) return;
    fetchSimulationData(selectedSimulationId);
    return () => {
      if (simulationAbortControllerRef.current) {
        simulationAbortControllerRef.current.abort();
      }
    };
  }, [selectedSimulationId, fetchSimulationData]);

  useEffect(() => {
    return () => {
      if (simulationAbortControllerRef.current) {
        simulationAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handlePreviousSimulation = () => {
    if (currentSimulationIndex < simulationList.length - 1) {
      const newIndex = currentSimulationIndex + 1;
      setCurrentSimulationIndex(newIndex);
      setSelectedSimulationId(simulationList[newIndex].simulation_id);
    }
  };

  const handleNextSimulation = () => {
    if (currentSimulationIndex > 0) {
      const newIndex = currentSimulationIndex - 1;
      setCurrentSimulationIndex(newIndex);
      setSelectedSimulationId(simulationList[newIndex].simulation_id);
    }
  };

  const handleSimulationSelect = (event) => {
    const selectedIndex = event.target.value;
    setCurrentSimulationIndex(selectedIndex);
    setSelectedSimulationId(simulationList[selectedIndex].simulation_id);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">Ładowanie...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  const teams = Object.keys(data.teamData).map(teamId => ({
    id: parseInt(teamId),
    name: data.teamData[teamId].name,
    expectedPoints: data.teamData[teamId].expectedPoints ?? 0,
    expectedPlace: data.teamData[teamId].expectedPlace ?? 0,
  })).sort((a, b) => a.expectedPlace - b.expectedPlace);

  const numTeams = teams.length;
  const positionMatrix = teams.map(team => {
    const probs = Array(numTeams).fill(0);
    data.positionProbabilities
      .filter(prob => prob.teamId === team.id)
      .forEach(prob => {
        probs[prob.position - 1] = prob.probability;
      });
    return { teamId: team.id, teamName: team.name, probabilities: probs };
  });

  return (
    <Box sx={{ padding: { xs: '2px', sm: '5px', md: '10px' } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column' },
          justifyContent: { xs: 'center', sm: 'space-between' },
          alignItems: 'center',
          mb: 2,
          gap: 1,
        }}
      >
        <Typography variant="h5" sx={{ textAlign: { xs: 'center', sm: 'center' }, fontSize: { xs: '1.0rem', sm: '1.2rem' } }}>
          Matryca Prawdopodobieństw - {leagueName}
        </Typography>
        {simulationList.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            <IconButton
              onClick={handlePreviousSimulation}
              disabled={currentSimulationIndex >= simulationList.length - 1}
              size="small"
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            <CustomSelect
			  value={currentSimulationIndex}
			  onChange={handleSimulationSelect}
			  options={simulationOptions}
			  //sx={{ mx: 1, minWidth: { xs: '150px', sm: '200px' } }} // Nadpisanie minWidth i dodanie mx: 1
			  displayEmpty // Przekazanie propów specyficznych dla Select
			  inputProps={{ 'aria-label': 'Wybierz symulację' }} // Przekazanie inputProps
			/>
            <IconButton
              onClick={handleNextSimulation}
              disabled={currentSimulationIndex === 0}
              size="small"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {numTeams === 0 ? (
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Brak danych symulacji dla tej ligi.
        </Typography>
      ) : (
        <>
          {simulationList[currentSimulationIndex]?.conditions !== "" && (
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Symulacja warunkowa!<br />
              <span dangerouslySetInnerHTML={{ __html: simulationList[currentSimulationIndex]?.conditions?.replace(/\n/g, '<br />') || '' }} />
            </Typography>
          )}
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, textAlign: 'center' }}>
            Prawdopodobieństwa zajęcia poszczególnych miejsc
          </Typography>

		  <RequireVip variant='lock-icon' requirement={selectedComp.stats_requirement}>
			  <TableContainer
				  component={Paper}
				  sx={{
					marginBottom: 1,
					margin: '0 auto',
					width: 'auto',
					maxWidth: 'fit-content', // Ogranicza rozszerzanie tabeli
					overflowX: 'auto',
					position: 'relative',
					
					borderRadius: '12px',
					WebkitOverflowScrolling: 'touch',
					'&::-webkit-scrollbar': { width: '8px', height: '8px' },
					'&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
					'&::before': {
					  content: '"SymuLiga.pl"',
					  position: 'absolute',
					  top: '60%',
					  left: '50%',
					  transform: 'translate(-50%, -50%) rotate(-45deg)',
					  fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
					  fontWeight: 'bold',
					  color: 'rgba(0, 0, 0, 0.15)',
					  pointerEvents: 'none',
					  zIndex: 0,
					  whiteSpace: 'nowrap',
					},
				  }}
				>
				  <Table
					sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse' }} // width: 'auto' dla ograniczenia rozszerzania
				  >
					<TableHead>
					  <TableRow>
						<TableCell
						  sx={{
							position: 'sticky',
							left: 0,
							top: 0,
							zIndex: 3,
							backgroundColor: 'background.paper',
							width: 'auto',
							maxWidth: '100%', // Maksymalna szerokość dopasowana do zawartości
							whiteSpace: 'normal', // Pozwala na zawijanie tekstu przy zwężaniu
							padding: '6px',
							fontSize: { xs: '0.8rem', sm: '0.9rem' },
							borderRight: '1px solid rgba(224, 224, 224, 1)',
						  }}
						>
						  Zespół \ Pozycja
						</TableCell>
						{Array.from({ length: numTeams }, (_, i) => (
						  <TableCell
							key={i}
							align="center"
							sx={{
							  position: 'sticky',
							  top: 0,
							  zIndex: 2,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Proporcjonalne rozciąganie
							  padding: '6px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderBottom: '2px solid rgba(224, 224, 224, 1)',
							}}
						  >
							{i + 1}
						  </TableCell>
						))}
					  </TableRow>
					</TableHead>
					<TableBody>
					  {positionMatrix.map(row => (
						<TableRow key={row.teamId}>
						  <TableCell component={NavLink}
			  to={`/team/${row.teamId}`}
							sx={{
							  position: 'sticky',
							  left: 0,
							  zIndex: 1,
							  backgroundColor: 'background.paper',
							  width: 'auto',
							  maxWidth: '100%', // Maksymalna szerokość dopasowana do zawartości
							  whiteSpace: 'normal', // Pozwala na zawijanie tekstu przy zwężaniu
							  padding: '6px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderRight: '1px solid rgba(224, 224, 224, 1)',
							}}
						  >
							{row.teamName}
						  </TableCell>
						  {row.probabilities.map((prob, index) => (
							<TableCell
							  key={index}
							  align="center"
							  sx={{
								backgroundColor: getGradientColor(prob),
								color: 'black',
								fontSize: { xs: '0.65rem', sm: '0.8rem' },
								padding: '3px',
								width: 'auto', // Proporcjonalne rozciąganie
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getGradientColor(Math.min(prob + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
							>
							  {(prob * 100).toFixed(1)}
							</TableCell>
						  ))}
						</TableRow>
					  ))}
					</TableBody>
				  </Table>
				</TableContainer>
			</RequireVip>
        </>
      )}
    </Box>
  );
};

export default PositionMatrix;